import { getAnalytics, setAnalyticsCollectionEnabled } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  indexedDBLocalPersistence,
  initializeAuth,
} from "firebase/auth";
import { connectAuthEmulator } from "firebase/auth";
import { connectDatabaseEmulator, getDatabase } from "firebase/database";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { createContext, useContext } from "react";

import {
  DATABASE_URL,
  EMULADORES,
  USANDO_EMULADORES,
} from "config/runtime";

import { aoMudarConsentimento, temConsentimentoAnalitico } from "utils/consent";
export const FirebaseContext = createContext({});

const VITE_CHAVE_API = import.meta.env.VITE_CHAVE_API
  ? import.meta.env.VITE_CHAVE_API
  : "AIzaSyBT9Wd3Tqxz-qBYWwjUOa58z0w4VTVSass";

const firebaseConfig = {
  apiKey: VITE_CHAVE_API,
  authDomain: "isugar-dev.firebaseapp.com",
  databaseURL: DATABASE_URL,
  projectId: "isugar-dev",
  storageBucket: "isugar-dev.appspot.com",
  messagingSenderId: "203676642700",
  appId: "1:203676642700:web:722513b923eb28e8e8baa9",
  measurementId: "G-J12Y5R0J1F",
};

const myApp = initializeApp(firebaseConfig);

// `getAuth` usaria a hierarquia padrão da web, com IndexedDB na frente. Isso
// gerava um erro recorrente no boot ("Database is closing/hidden"): o SDK checa
// `_isAvailable()` (abre o banco), escolhe o IndexedDB e, milissegundos depois,
// tenta ler a sessão — se o navegador tiver congelado/destruído o documento
// nesse intervalo (aba ao fundo, bfcache, webview in-app, usuário saindo no meio
// do carregamento), as 4 tentativas de reabrir falham e a inicialização rejeita.
// Como a rejeição vira uma promise solta dentro do `onAuthStateChanged` do SDK,
// o callback nunca dispara e o app trava no spinner do authProvider.
//
// O localStorage é síncrono e não tem esse modo de falha, e já era a persistência
// que este arquivo pedia via `setPersistence`. O IndexedDB continua na lista
// porque o SDK varre toda a hierarquia atrás de sessões já salvas e as migra.
const myAuth = initializeAuth(myApp, {
  persistence: [browserLocalPersistence, indexedDBLocalPersistence],
});
const myFirestore = getFirestore(myApp);
const myRtdb = getDatabase(myApp);
const myAnalytics = getAnalytics(myApp);

// Emuladores locais para os testes E2E. `USANDO_EMULADORES` já exige a
// bandeira explícita **e** o modo de desenvolvimento, então este bloco é
// inerte em qualquer build publicado — nenhum teste toca produção.
if (USANDO_EMULADORES) {
  connectAuthEmulator(myAuth, EMULADORES.authUrl, { disableWarnings: true });
  connectFirestoreEmulator(
    myFirestore,
    EMULADORES.firestore.host,
    EMULADORES.firestore.port,
  );
  connectDatabaseEmulator(
    myRtdb,
    EMULADORES.database.host,
    EMULADORES.database.port,
  );
  // eslint-disable-next-line no-console
  console.warn(
    "[iSugar] Emuladores do Firebase ligados — nenhum dado vai para produção.",
  );
}

// Coleta desligada até o usuário aceitar cookies analíticos. Usar o próprio
// interruptor do Firebase, e não um guard em cada chamada, mantém os 42
// `logEvent` do events.js intactos: eles viram no-op enquanto estiver off.
setAnalyticsCollectionEnabled(myAnalytics, temConsentimentoAnalitico());
aoMudarConsentimento((aceito) =>
  setAnalyticsCollectionEnabled(myAnalytics, aceito),
);

// Exportar instâncias para uso direto em serviços
export { myAnalytics, myApp, myAuth, myFirestore, myRtdb };

export const FirebaseProvider = ({ children }) => {
  return (
    <FirebaseContext.Provider
      value={{
        myApp,
        myAuth,
        myFirestore,
        myRtdb,
        myAnalytics,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = () => {
  return useContext(FirebaseContext);
};
